import React, { FC, useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { CatalogColumn, CatalogFilterOption, DateRangeFilterValue, FilterOptionValue, FilterValue } from './types'
import {
  hasActiveFilterValue,
  isDateRangeColumn,
  isDateRangeFilterValue,
  resolveColumnFilterInputType,
} from './filterUtils'

interface ColumnMenuPopoverProps {
  column: CatalogColumn
  isOpen: boolean
  onClose: () => void
  onSortAsc: () => void
  onSortDesc: () => void
  onAutosize: () => void
  onAutosizeAll: () => void
  onFilterChange?: (value: FilterValue | '', operator?: string) => void
  currentFilter?: FilterValue | ''
  currentOperator?: string
  anchorEl: HTMLElement | null
}

const MENU_WIDTH = 180
const FILTER_PANEL_WIDTH = 260
const VIEWPORT_PADDING = 8

const getScrollParents = (node: HTMLElement | null): (HTMLElement | Window)[] => {
  const scrollParents: (HTMLElement | Window)[] = [window]
  if (!node) return scrollParents

  let parent = node.parentElement
  while (parent) {
    const style = window.getComputedStyle(parent)
    const overflow = `${style.overflow}${style.overflowX}${style.overflowY}`
    if (/(auto|scroll|overlay)/.test(overflow)) {
      scrollParents.push(parent)
    }
    parent = parent.parentElement
  }

  return scrollParents
}

export const ColumnMenuPopover: FC<ColumnMenuPopoverProps> = ({
  column,
  isOpen,
  onClose,
  onSortAsc,
  onSortDesc,
  onAutosize,
  onAutosizeAll,
  onFilterChange,
  currentFilter = '',
  currentOperator = 'contains',
  anchorEl,
}) => {
  const [showFilter, setShowFilter] = useState(false)
  const [filterValue, setFilterValue] = useState<FilterValue | ''>(currentFilter)
  const [filterOperator, setFilterOperator] = useState(currentOperator)
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setFilterValue(currentFilter)
  }, [currentFilter])

  useEffect(() => {
    setFilterOperator(currentOperator || 'contains')
  }, [currentOperator])

  useEffect(() => {
    if (!isOpen) {
      setShowFilter(false)
    }
  }, [isOpen])

  const updatePosition = useCallback(() => {
    if (!anchorEl) return

    const rect = anchorEl.getBoundingClientRect()
    const isOutOfView =
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth

    if (isOutOfView) {
      onClose()
      setShowFilter(false)
      return
    }

    const width = showFilter ? FILTER_PANEL_WIDTH : MENU_WIDTH
    let left = Math.max(VIEWPORT_PADDING, rect.left)
    if (left + width > window.innerWidth - VIEWPORT_PADDING) {
      left = Math.max(VIEWPORT_PADDING, rect.right - width)
    }

    const estimatedHeight = showFilter ? 320 : 220
    let top = rect.bottom + 5
    if (top + estimatedHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, rect.top - estimatedHeight - 5)
    }

    setPopoverPosition({ top, left })
  }, [anchorEl, onClose, showFilter])

  useEffect(() => {
    if (!isOpen || !anchorEl) {
      setPopoverPosition(null)
      return
    }

    updatePosition()

    const handleScrollOrResize = () => {
      updatePosition()
    }

    const scrollParents = getScrollParents(anchorEl)

    window.addEventListener('resize', handleScrollOrResize)
    scrollParents.forEach((target) => {
      target.addEventListener('scroll', handleScrollOrResize, { passive: true })
    })

    return () => {
      window.removeEventListener('resize', handleScrollOrResize)
      scrollParents.forEach((target) => {
        target.removeEventListener('scroll', handleScrollOrResize)
      })
    }
  }, [isOpen, anchorEl, updatePosition])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose()
        setShowFilter(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, anchorEl])

  if (!isOpen || !anchorEl || !popoverPosition) return null

  const popoverWidth = showFilter ? FILTER_PANEL_WIDTH : MENU_WIDTH
  const style: React.CSSProperties = {
    position: 'fixed',
    top: popoverPosition.top,
    left: popoverPosition.left,
    zIndex: 1050,
    width: popoverWidth,
    minWidth: popoverWidth,
  }

  const usesSelectFilter = resolveColumnFilterInputType(column) === 'SELECT'
  const usesDateRangeFilter = isDateRangeColumn(column)
  const selectableFilterOptions = Array.isArray(column.filterOptions) ? column.filterOptions : []
  const getDateRangeValue = (value: FilterValue | ''): DateRangeFilterValue =>
    isDateRangeFilterValue(value) ? value : {}
  const dateRangeValue = getDateRangeValue(filterValue)
  const operatorOptions = Array.from(
    new Set([
      ...(Array.isArray(column.filterOperators) && column.filterOperators.length > 0
        ? column.filterOperators
        : ['contains']),
      filterOperator || 'contains',
    ])
  )
  const hasActiveFilter = hasActiveFilterValue(currentFilter)
  const formatOperatorLabel = (operator: string) =>
    operator
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  const usesNumericFilter = operatorOptions.every((operator) =>
    ['equals', 'greater_than', 'less_than'].includes(operator)
  )
  const optionKey = (value: FilterOptionValue) => {
    if (typeof value === 'boolean') return `bool:${value ? '1' : '0'}`
    if (typeof value === 'number') return `num:${value}`
    return `str:${value}`
  }
  const selectItems: Array<{ key: string; label: string; value: FilterOptionValue }> =
    selectableFilterOptions.map((option) => {
      if (typeof option === 'object' && option !== null && 'value' in option) {
        const typedOption = option as CatalogFilterOption
        return {
          key: optionKey(typedOption.value),
          label: String(typedOption.label ?? typedOption.value),
          value: typedOption.value,
        }
      }
      const primitive = option as FilterOptionValue
      return {
        key: optionKey(primitive),
        label: String(primitive),
        value: primitive,
      }
    })

  const handleApplyFilter = () => {
    if (!onFilterChange) {
      setShowFilter(false)
      return
    }

    if (usesDateRangeFilter) {
      const range = getDateRangeValue(filterValue)
      if (!range.from && !range.to) {
        if (hasActiveFilter) onFilterChange('', 'between')
        setShowFilter(false)
        return
      }
      onFilterChange(range, 'between')
    } else if (usesSelectFilter) {
      if (!hasActiveFilterValue(filterValue)) {
        if (hasActiveFilter) onFilterChange('')
        setShowFilter(false)
        return
      }
      onFilterChange(filterValue, 'equals')
    } else if (!hasActiveFilterValue(filterValue)) {
      if (hasActiveFilter) onFilterChange('', filterOperator)
      setShowFilter(false)
      return
    } else {
      onFilterChange(filterValue, filterOperator)
    }
    setShowFilter(false)
  }

  const handleClearFilter = () => {
    setFilterValue('')
    if (onFilterChange && hasActiveFilter) {
      onFilterChange('')
    }
    setShowFilter(false)
  }

  const menuItemStyle: React.CSSProperties = {
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    borderRadius: '4px',
  }

  return createPortal(
    <>
      <style>
        {`
          .column-menu-item {
            transition: all 0.2s ease;
            cursor: pointer;
            border-radius: 4px;
          }
          .column-menu-item:hover {
            background-color: #f5f8fa !important;
            transform: translateX(4px);
            padding-left: 1.25rem !important;
          }
          .column-menu-item:active {
            transform: translateX(2px);
          }
        `}
      </style>
      <div
        ref={popoverRef}
        className="card shadow-sm column-menu-popover"
        style={style}
      >
        {!showFilter ? (
          <div className="py-2">
            {/* Sort actions */}
            {column.sortable !== false && (
              <>
                <button
                  className="dropdown-item column-menu-item d-flex align-items-center gap-2 px-4 py-2"
                  onClick={() => {
                    onSortAsc()
                    onClose()
                  }}
                >
                  <i className="bi bi-sort-alpha-down"></i>
                  Sort Ascending
                </button>
                <button
                  className="dropdown-item column-menu-item d-flex align-items-center gap-2 px-4 py-2"
                  onClick={() => {
                    onSortDesc()
                    onClose()
                  }}
                >
                  <i className="bi bi-sort-alpha-down-alt"></i>
                  Sort Descending
                </button>
                <div className="dropdown-divider"></div>
              </>
            )}

            {/* Filter action */}
            {column.filterable !== false && onFilterChange && (
              <>
                <button
                  className="dropdown-item column-menu-item d-flex align-items-center gap-2 px-4 py-2"
                  onClick={() => setShowFilter(true)}
                >
                  <i className="bi bi-funnel"></i>
                  Filter
                  {hasActiveFilter && (
                    <span className="badge badge-primary ms-auto">Active</span>
                  )}
                </button>
                <div className="dropdown-divider"></div>
              </>
            )}

            {/* Autosize actions */}
            {column.resizable !== false && (
              <>
                <button
                  className="dropdown-item column-menu-item d-flex align-items-center gap-2 px-4 py-2"
                  onClick={() => {
                    onAutosize()
                    onClose()
                  }}
                >
                  <i className="bi bi-arrows-expand"></i>
                  Autosize This Column
                </button>
                <button
                  className="dropdown-item column-menu-item d-flex align-items-center gap-2 px-4 py-2"
                  onClick={() => {
                    onAutosizeAll()
                    onClose()
                  }}
                >
                  <i className="bi bi-arrows-expand"></i>
                  Autosize All Columns
                </button>
              </>
            )}
          </div>
      ) : (
        <div className="p-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0">Filter</h6>
            <button
              className="btn btn-sm btn-icon"
              onClick={() => setShowFilter(false)}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>

          {!usesSelectFilter && !usesDateRangeFilter && (
            <div className="mb-3">
              <label className="form-label small text-muted">Operator</label>
              <select
                className="form-select form-select-sm"
                value={filterOperator}
                onChange={(e) => setFilterOperator(e.target.value)}
              >
                {operatorOptions.map((operator) => (
                  <option key={operator} value={operator}>
                    {formatOperatorLabel(operator)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label small text-muted">Value</label>
            {usesDateRangeFilter ? (
              <div className="d-flex flex-column gap-2">
                <div>
                  <label className="form-label small text-muted mb-1">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateRangeValue.from || ''}
                    onChange={(e) => setFilterValue({ ...dateRangeValue, from: e.target.value })}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="form-label small text-muted mb-1">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateRangeValue.to || ''}
                    min={dateRangeValue.from || undefined}
                    onChange={(e) => setFilterValue({ ...dateRangeValue, to: e.target.value })}
                  />
                </div>
              </div>
            ) : usesSelectFilter ? (
              <select
                className="form-select form-select-sm"
                value={filterValue === '' ? '' : optionKey(filterValue as FilterOptionValue)}
                onChange={(e) => {
                  const selected = selectItems.find((item) => item.key === e.target.value)
                  setFilterValue(selected ? selected.value : '')
                }}
                autoFocus
              >
                <option value="">Select value...</option>
                {selectItems.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={usesNumericFilter ? 'number' : 'text'}
                className="form-control form-control-sm"
                placeholder="Filter value..."
                value={
                  filterValue === '' || isDateRangeFilterValue(filterValue)
                    ? ''
                    : String(filterValue)
                }
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyFilter()
                  }
                }}
                autoFocus
              />
            )}
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-light flex-fill"
              onClick={handleClearFilter}
            >
              Clear
            </button>
            <button
              className="btn btn-sm btn-primary flex-fill"
              onClick={handleApplyFilter}
            >
              Apply
            </button>
          </div>
          </div>
        )}
      </div>
    </>,
    document.body
  )
}
